import api from './api.service';
import type { ApiResponse } from '@/types';
import type {
  CustomerPropertyAllocation,
  CustomerPropertyException,
  CustomerPropertyReceipt,
  CustomerPropertyReceiptLine,
  CustomerPropertyReconciliation,
  CustomerPropertyRequirement,
  CustomerPropertyReturn,
  ReconciliationPreview,
} from '@/constants/cspm';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type CspmListFilters = {
  customer_id?: string;
  status?: string;
  source_document_type?: string;
  source_document_id?: string;
  requirement_id?: string;
  receipt_id?: string;
  attention?: boolean | string | number;
  skip?: number;
  take?: number;
};

class CspmService {
  // —— Source ——
  async validateSource(body: {
    source_document_type: string;
    source_document_id: string;
    customer_id?: string;
  }) {
    const res = await api.post('/cspm/sources/validate', body);
    return unwrap<Record<string, unknown>>(res);
  }

  // —— Requirements ——
  async listRequirements(filters: CspmListFilters = {}) {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/requirements?${q}`);
    return unwrap<{ rows: CustomerPropertyRequirement[]; total: number }>(res) ?? { rows: [], total: 0 };
  }

  async getRequirement(id: string) {
    const res = await api.get(`/cspm/requirements/${id}`);
    return unwrap<CustomerPropertyRequirement>(res);
  }

  async createRequirement(body: Record<string, unknown>) {
    const res = await api.post('/cspm/requirements', body);
    return unwrap<CustomerPropertyRequirement>(res);
  }

  async updateRequirement(id: string, body: Record<string, unknown>) {
    const res = await api.put(`/cspm/requirements/${id}`, body);
    return unwrap<CustomerPropertyRequirement>(res);
  }

  async openRequirement(id: string) {
    const res = await api.post(`/cspm/requirements/${id}/open`);
    return unwrap<CustomerPropertyRequirement>(res);
  }

  async closeRequirement(id: string) {
    const res = await api.post(`/cspm/requirements/${id}/close`);
    return unwrap<CustomerPropertyRequirement>(res);
  }

  async cancelRequirement(id: string) {
    const res = await api.post(`/cspm/requirements/${id}/cancel`);
    return unwrap<CustomerPropertyRequirement>(res);
  }

  // —— Receipts ——
  async listReceipts(filters: CspmListFilters = {}) {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/receipts?${q}`);
    return unwrap<{ rows: CustomerPropertyReceipt[]; total: number }>(res) ?? { rows: [], total: 0 };
  }

  async getReceipt(id: string) {
    const res = await api.get(`/cspm/receipts/${id}`);
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async createReceipt(body: Record<string, unknown>) {
    const res = await api.post('/cspm/receipts', body);
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async updateReceipt(id: string, body: Record<string, unknown>) {
    const res = await api.put(`/cspm/receipts/${id}`, body);
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async postReceipt(id: string, body?: { idempotency_key?: string }) {
    const res = await api.post(`/cspm/receipts/${id}/post`, body ?? {});
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async cancelReceipt(id: string) {
    const res = await api.post(`/cspm/receipts/${id}/cancel`);
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async releaseReceipt(id: string) {
    const res = await api.post(`/cspm/receipts/${id}/release`);
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async failReceipt(id: string, body?: { remarks?: string }) {
    const res = await api.post(`/cspm/receipts/${id}/fail`, body ?? {});
    return unwrap<CustomerPropertyReceipt>(res);
  }

  async listAllocationsByReceipt(receiptId: string) {
    const res = await api.get(`/cspm/receipts/${receiptId}/allocations`);
    return unwrap<CustomerPropertyAllocation[]>(res) ?? [];
  }

  async listAllocationHistoryByReceipt(receiptId: string) {
    const res = await api.get(`/cspm/receipts/${receiptId}/allocation-history`);
    return (
      unwrap<
        Array<{
          id: string;
          allocation_id: string;
          from_document_type?: string | null;
          from_document_id?: string | null;
          to_document_type: string;
          to_document_id: string;
          quantity: number;
          reason?: string | null;
          created_at?: string;
        }>
      >(res) ?? []
    );
  }

  // —— Allocations ——
  async allocate(body: {
    receipt_line_id: string;
    quantity: number;
    target_document_type: string;
    target_document_id: string;
    remarks?: string | null;
    soft_reserve?: boolean;
    uom?: string;
  }) {
    const res = await api.post('/cspm/allocations', body);
    return unwrap<CustomerPropertyAllocation>(res);
  }

  async transferAllocation(
    id: string,
    body: {
      quantity?: number;
      /** Preferred API field names */
      to_document_type?: string;
      to_document_id?: string;
      /** Accepted aliases (mapped to to_*) */
      target_document_type?: string;
      target_document_id?: string;
      remarks?: string | null;
      reason?: string | null;
    }
  ) {
    const payload = {
      quantity: body.quantity,
      to_document_type: body.to_document_type || body.target_document_type,
      to_document_id: body.to_document_id || body.target_document_id,
      reason: body.reason ?? body.remarks ?? null,
    };
    const res = await api.post(`/cspm/allocations/${id}/transfer`, payload);
    return unwrap<CustomerPropertyAllocation>(res);
  }

  // —— Exceptions ——
  async listExceptions(filters: CspmListFilters = {}) {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/exceptions?${q}`);
    return unwrap<{ rows: CustomerPropertyException[]; total: number }>(res) ?? { rows: [], total: 0 };
  }

  async createException(body: Record<string, unknown>) {
    const res = await api.post('/cspm/exceptions', body);
    return unwrap<CustomerPropertyException>(res);
  }

  async getException(id: string) {
    const res = await api.get(`/cspm/exceptions/${id}`);
    return unwrap<CustomerPropertyException & { dispositions?: Array<Record<string, unknown>> }>(
      res
    );
  }

  async addDisposition(id: string, body: Record<string, unknown>) {
    const res = await api.post(`/cspm/exceptions/${id}/dispositions`, body);
    return unwrap<Record<string, unknown>>(res);
  }

  async createNcrFromException(id: string, body?: { description?: string; severity?: string }) {
    const res = await api.post(`/cspm/exceptions/${id}/create-ncr`, body ?? {});
    return unwrap<{ exception: CustomerPropertyException; ncr: Record<string, unknown> }>(res);
  }

  async linkNcrToException(id: string, ncrId: string) {
    const res = await api.post(`/cspm/exceptions/${id}/link-ncr`, { ncr_id: ncrId });
    return unwrap<CustomerPropertyException>(res);
  }

  async closeException(id: string) {
    const res = await api.post(`/cspm/exceptions/${id}/close`);
    return unwrap<CustomerPropertyException>(res);
  }

  async getPassport(receiptId: string) {
    const res = await api.get(`/cspm/receipts/${receiptId}/passport`);
    return unwrap<{
      receipt: CustomerPropertyReceipt;
      allocations: CustomerPropertyAllocation[];
      allocation_history: Array<Record<string, unknown>>;
      exceptions: CustomerPropertyException[];
      material_issuances: Array<Record<string, unknown>>;
      custody: Array<{
        line_id: string;
        line_number: number;
        description: string;
        purpose: string;
        classification: string;
        custody_status: string;
        asset_id: string | null;
        maintenance_case_id: string | null;
        serial_number: string | null;
        workshop_path: string | null;
        asset_path: string | null;
        calibration_eligibility?: {
          eligible: boolean;
          reason: string | null;
          valid_until: string | null;
          next_due: string | null;
        } | null;
      }>;
      genealogy: {
        heats: string[];
        serials: string[];
        matrix_path: string;
        cpr_filter?: string;
        tree?: Record<string, unknown>;
        outputs?: unknown[];
        deep_links?: { by_cpr?: string };
      };
      foc_evidence?: {
        matches?: Array<Record<string, unknown>>;
        note?: string;
      };
    }>(res);
  }

  async openCustody(receiptId: string, lineId: string) {
    const res = await api.post(`/cspm/receipts/${receiptId}/lines/${lineId}/open-custody`);
    return unwrap<CustomerPropertyReceiptLine>(res);
  }

  async syncCustody(receiptId: string, lineId: string) {
    const res = await api.post(`/cspm/receipts/${receiptId}/lines/${lineId}/sync-custody`);
    return unwrap<CustomerPropertyReceiptLine>(res);
  }

  // —— Returns ——
  async listReturns(filters: CspmListFilters = {}) {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/returns?${q}`);
    return unwrap<{ rows: CustomerPropertyReturn[]; total: number }>(res) ?? { rows: [], total: 0 };
  }

  async createReturn(body: Record<string, unknown>) {
    const res = await api.post('/cspm/returns', body);
    return unwrap<CustomerPropertyReturn>(res);
  }

  async updateReturn(id: string, body: Record<string, unknown>) {
    const res = await api.put(`/cspm/returns/${id}`, body);
    return unwrap<CustomerPropertyReturn>(res);
  }

  async getReturn(id: string) {
    const res = await api.get(`/cspm/returns/${id}`);
    return unwrap<CustomerPropertyReturn>(res);
  }

  async postReturn(
    id: string,
    body?: { idempotency_key?: string; override?: boolean; override_reason?: string }
  ) {
    const res = await api.post(`/cspm/returns/${id}/post`, body ?? {});
    return unwrap<CustomerPropertyReturn>(res);
  }

  async issueReturnAckLink(id: string) {
    const res = await api.post(`/cspm/returns/${id}/acknowledgement-link`);
    return unwrap<CustomerPropertyReturn & { acknowledgement_url_token: string }>(res);
  }

  async acknowledgeReturnInPerson(
    id: string,
    body: {
      acknowledged_by_name: string;
      acknowledgement_signature_document_id?: string | null;
      acknowledgement_notes?: string | null;
    }
  ) {
    const res = await api.post(`/cspm/returns/${id}/acknowledge`, body);
    return unwrap<CustomerPropertyReturn>(res);
  }

  async getPublicReturnAck(token: string) {
    const res = await api.get(`/cspm/public/returns/${token}/acknowledge`);
    return unwrap<{
      id: string;
      return_number: string;
      customer_ack_name: string | null;
      customer_ack_at: string | null;
      already_acknowledged: boolean;
      lines: Array<{ description: string; quantity: number; uom: string; serial_number?: string }>;
    }>(res);
  }

  async postPublicReturnAck(
    token: string,
    body: {
      acknowledged_by_name: string;
      acknowledgement_signature_document_id?: string | null;
      acknowledgement_notes?: string | null;
    }
  ) {
    const res = await api.post(`/cspm/public/returns/${token}/acknowledge`, body);
    return unwrap<Record<string, unknown>>(res);
  }

  // —— Reconciliations ——
  async previewReconciliation(params: {
    source_document_type: string;
    source_document_id: string;
    customer_id?: string;
  }) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/reconciliations/preview?${q}`);
    return unwrap<ReconciliationPreview>(res);
  }

  async createReconciliation(body: {
    source_document_type: string;
    source_document_id: string;
    customer_id?: string;
    variance_approved?: boolean;
    variance_reason?: string | null;
  }) {
    const res = await api.post('/cspm/reconciliations', body);
    return unwrap<CustomerPropertyReconciliation>(res);
  }

  async getReconciliation(id: string) {
    const res = await api.get(`/cspm/reconciliations/${id}`);
    return unwrap<CustomerPropertyReconciliation>(res);
  }

  async checkClosure(params: { source_document_type: string; source_document_id: string }) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/closure/check?${q}`);
    return unwrap<{
      has_unresolved: boolean;
      messages: string[];
      policy: string;
      open_receipts: number;
      open_exceptions: number;
      open_allocations: number;
      customer_stock_on_hand: number;
      open_reconciliations_needed: boolean;
    }>(res);
  }

  // —— Portal ——
  async issuePortalToken(body: {
    customer_id: string;
    scopes?: string | string[];
    expires_days?: number;
  }) {
    const res = await api.post('/cspm/portal/tokens', body);
    return unwrap<{
      token: string;
      expires_at: string;
      portal_path: string;
      scopes: string;
      customer_id: string;
    }>(res);
  }

  async listPortalTokens(filters: { customer_id?: string } = {}) {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/portal/tokens?${q}`);
    return unwrap<Array<Record<string, unknown>>>(res) ?? [];
  }

  async revokePortalToken(id: string) {
    const res = await api.post(`/cspm/portal/tokens/${id}/revoke`);
    return unwrap<{ id: string; revoked: boolean }>(res);
  }

  async getPublicPortal(token: string) {
    const res = await api.get(`/cspm/public/portal/${token}`);
    return unwrap<{
      customer: { id: string; customer_name: string; customer_code?: string | null };
      scopes: string;
      open_receipts: Array<Record<string, unknown>>;
      on_hand: Array<Record<string, unknown>>;
      note?: string;
    }>(res);
  }

  async getPublicPortalPassport(token: string, receiptId: string) {
    const res = await api.get(`/cspm/public/portal/${token}/receipts/${receiptId}/passport`);
    return unwrap<Record<string, unknown>>(res);
  }

  // —— FOC ↔ CPR advisory ——
  async getFocCprAdvisory(params: { customer_id?: string; project_id?: string } = {}) {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/cspm/advisory/foc-cpr?${q}`);
    return unwrap<{
      matches: Array<Record<string, unknown>>;
      note: string;
      foc_count: number;
      cpr_line_count: number;
    }>(res);
  }

  async listPreservationDue(params: { within_days?: number } = {}) {
    const q = new URLSearchParams();
    if (params.within_days != null) q.set('within_days', String(params.within_days));
    const res = await api.get(`/cspm/preservation/due?${q}`);
    return unwrap<{ rows: Array<Record<string, unknown>>; total: number }>(res) ?? {
      rows: [],
      total: 0,
    };
  }
}

const cspmService = new CspmService();
export default cspmService;
